import { useState, useEffect, useMemo } from 'react';
import { useMutation } from '@apollo/client';

import useSuspenseQuery from 'commons/useSuspenseQuery';
import { PRODUCT_FAVORITES_META } from 'data-hooks/queries';
import browserStorage from 'components/consumer/browser-storage';
import useSetFavoriteModalState from 'global-state/favoriteModalState/useSetFavoriteModalState';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import { FAVORITE_PRODUCT } from '../../mocks/mutations';
import useUserData from '../useUserData';
import useProductPrice from '../useProductPrice';
import useProductBasicDetails from '../useProductBasicDetails';
import useAddToWishList from '../useEvents/useAddToWishList';

const FAV_ELIGIBLE_OPTIONS = {
  Fabric: true,
  Leather: true,
  Wood: true,
  Frame: true,
};

const getFavSwatchOption = (options, selectedOptions) => {
  const eligibleOption = options.find(({ name }) => FAV_ELIGIBLE_OPTIONS[name]);
  if (!eligibleOption) {
    return null;
  }
  const eligibleOptionSelection = selectedOptions[eligibleOption.id];
  const eligibleOptionValue = eligibleOption.values.find(
    ({ id }) => id === eligibleOptionSelection
  );

  return {
    optionName: eligibleOptionValue.value,
    optionId: eligibleOptionValue.id,
    optionType: eligibleOption.name,
  };
};

const checkIsFavorited = (options, selectedOptionsExtended, customerMeta) => {
  if (!options || !selectedOptionsExtended || !customerMeta) {
    return false;
  }
  const eligibleOption = options.find(({ name }) => FAV_ELIGIBLE_OPTIONS[name]);
  if (!eligibleOption) {
    return customerMeta.favorited;
  }
  for (const favOption of customerMeta.favOptions) {
    const { optionId: valueId, optionType: optionName } = favOption;
    const option = options.find(({ name }) => name === optionName);
    const selection = selectedOptionsExtended[option.id];
    if (!selection) {
      return false;
    }
    if (selection.value.id === valueId) {
      return true;
    }
  }
  return false;
};

const useProductFavorite = (id, slug) => {
  const [isFavoriteClick, setFavoriteClick] = useState(false);
  const [isFavoritedTemp, setIsFavoritedTemp] = useState(null);
  let tempFavoritedTimeout = null;

  const { isLoggedIn } = useUserData();
  const { data: optionData } = usePDPOptions();
  const { data: priceData } = useProductPrice(id, slug);
  const { data: basicDetails, refetch } = useProductBasicDetails(id, slug);

  const {
    addToWishListEvent,
    getAddToWishListEventData,
    removeFromWishListEvent,
  } = useAddToWishList(
    basicDetails,
    optionData,
    priceData,
    priceData?.isClearance
  );

  const { data: favData, refetch: refetchFavorites } = useSuspenseQuery(
    PRODUCT_FAVORITES_META,
    {
      variables: {
        id: basicDetails?.id,
      },
      skip: !basicDetails?.id,
      ssr: false,
    }
  );
  const [favoriteProductMutation] = useMutation(FAVORITE_PRODUCT, {
    onCompleted: () => {
      refetchFavorites();
    },
  });

  const setFavoriteModalState = useSetFavoriteModalState();

  const { selectedOptions, selectedOptionsExtended, options } =
    optionData || {};
  const { id: productId, name: productName, slug: productSlug } =
    basicDetails || {};
  const { meta: favMeta } = favData?.productFavorites || {};

  const isFavorited = useMemo(
    () => checkIsFavorited(options, selectedOptionsExtended, favMeta?.customer),
    [options, selectedOptionsExtended, favMeta?.customer]
  );

  const addToFav = async () => {
    const swatchOption = getFavSwatchOption(options, selectedOptions);
    const favoritedTemp = !isFavoritedTemp ? !isFavorited : !isFavoritedTemp;
    setIsFavoritedTemp(favoritedTemp);
    if (!tempFavoritedTimeout) {
      clearTimeout(tempFavoritedTimeout);
      tempFavoritedTimeout = null;
    }
    setTimeout(() => setIsFavoritedTemp(null), 5000);
    await favoriteProductMutation({
      variables: {
        productID: productId,
        options: swatchOption,
      },
      refetchQueries: ['getFavoritedProducts'],
    });
    await refetch();
    if (favoritedTemp ?? isFavorited) {
      addToWishListEvent();
    } else {
      removeFromWishListEvent();
    }
  };

  useEffect(() => {
    if (isLoggedIn && isFavoriteClick) {
      addToFav();
    }
    // Adding all dependencies here would add extra complexity to the hook,
    // ideally we should wait until useEvent is available from React then use
    // that instead of useEffect here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const onFavClick = async () => {
    if (!isLoggedIn) {
      setFavoriteClick(true);

      await setFavoriteModalState({
        productId,
        productSlug,
        productName,
        swatchOption: JSON.stringify(
          getFavSwatchOption(options, selectedOptions)
        ),
        wlSegmentData: JSON.stringify(getAddToWishListEventData().heapData),
      });

      browserStorage().setItem('favoriteClickedProduct', productId);
    } else {
      await addToFav();
    }
  };

  return {
    isFavorited: isFavoritedTemp ?? isFavorited,
    onFavClick,
  };
};

export default useProductFavorite;
